/**
 * Championship Firestore Service — Steward Admin
 * Multi-championship CRUD operations.
 *
 * Data lives in top-level `championships` collection.
 * Sub-entities (drivers, events, results, penalties, leaderboards, sponsors,
 * rules, media) remain flat collections under `championships/data/*` with a
 * `championship` foreign-key on each document.
 *
 * Sponsors carry a `championships` array so they can be shared across
 * multiple championships.
 */
import { db, functions } from './firebase';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  onSnapshot, serverTimestamp, Timestamp, arrayUnion, arrayRemove,
  query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// ── Collection Paths ──
// Top-level championship docs
const champCol       = collection(db, 'championships');
// Flat sub-collections under `championships/data/…`
const driversCol     = collection(db, 'championships', 'data', 'drivers');
const eventsCol      = collection(db, 'championships', 'data', 'events');
const resultsCol     = collection(db, 'championships', 'data', 'results');
const penaltiesCol   = collection(db, 'championships', 'data', 'penalties');
const leaderboardCol = collection(db, 'championships', 'data', 'leaderboards');
const sponsorsCol    = collection(db, 'championships', 'data', 'sponsors');
const rulesCol       = collection(db, 'championships', 'data', 'rules');
const prizesCol      = collection(db, 'championships', 'data', 'prizes');
// const mediaCol    = collection(db, 'championships', 'data', 'media'); // future

/* Scoped queries, sorted in JS on purpose.

   These listeners used to download an entire collection and filter it in the
   browser, so cost grew with every season ever run. A single equality filter
   uses Firestore's automatic single-field index, but adding orderBy on a
   different field would demand a composite index — and a missing composite
   index makes the query throw rather than degrade. Filtering on the server and
   sorting the (already small) result set here keeps both problems away. */

// ── Helpers ──
function cleanData(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
}

/** Auto-compute status from start/end dates */
export function autoStatus(startDate, endDate) {
  if (!startDate) return 'upcoming';
  const now = Date.now();
  const start = startDate instanceof Timestamp
    ? startDate.toMillis()
    : new Date(startDate).getTime();
  const end = endDate
    ? (endDate instanceof Timestamp ? endDate.toMillis() : new Date(endDate).getTime())
    : null;
  if (now < start) return 'upcoming';
  if (end && now > end) return 'completed';
  return 'active';
}

// ── Scoring Constants ──
export const POSITION_POINTS = [25, 18, 15, 12, 10, 8, 6, 4];
export const FASTEST_LAP_BONUS = 1;

export const TITLES = [
  { id: 'granTurismo7', name: 'Gran Turismo 7', short: 'GT7' },
  { id: 'f1',          name: 'F1',              short: 'F1'  },
  { id: 'marioKart',   name: 'Mario Kart',      short: 'MK'  },
];

export const SERIES_FORMATS = [
  { id: 'points',      label: 'Points-Based',    description: 'Cumulative points across all rounds' },
  { id: 'elimination', label: 'Elimination',      description: 'Drivers eliminated each round' },
  { id: 'bestOf',      label: 'Best-of Series',   description: 'Best of N rounds determines the winner' },
  { id: 'custom',      label: 'Custom',           description: 'Custom format — define your own rules' },
];

// =============================================
// CHAMPIONSHIPS
// =============================================

/** Real-time listener for ALL championships, newest first */
export function onChampionships(cb) {
  return onSnapshot(champCol, snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    all.sort((a, b) => {
      const aT = a.startDate?.toMillis ? a.startDate.toMillis() : (a.startDate ? new Date(a.startDate).getTime() : 0);
      const bT = b.startDate?.toMillis ? b.startDate.toMillis() : (b.startDate ? new Date(b.startDate).getTime() : 0);
      return bT - aT;
    });
    cb(all);
  }, err => console.error('Championships query error:', err));
}

/** Real-time listener for currently active championships (date-based) */
export function onActiveChampionships(cb) {
  return onSnapshot(champCol, snap => {
    const now = Date.now();
    const active = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(c => {
        const start = c.startDate?.toMillis ? c.startDate.toMillis() : (c.startDate ? new Date(c.startDate).getTime() : null);
        const end = c.endDate?.toMillis ? c.endDate.toMillis() : (c.endDate ? new Date(c.endDate).getTime() : null);
        if (!start) return false;
        return now >= start && (!end || now <= end);
      });
    cb(active);
  }, err => console.error('Active championships query error:', err));
}

/** Get a single championship by ID */
export async function getChampionship(id) {
  const d = await getDoc(doc(champCol, id));
  return d.exists() ? { id: d.id, ...d.data() } : null;
}

export async function createChampionship(data) {
  const ref = await addDoc(champCol, cleanData({
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
  return ref.id;
}

export async function updateChampionship(id, data) {
  await updateDoc(doc(champCol, id), cleanData({ ...data, updatedAt: serverTimestamp() }));
}

export async function deleteChampionship(id) {
  await deleteDoc(doc(champCol, id));
}

// =============================================
// DRIVERS
// =============================================
export function onDrivers(championshipId, cb) {
  return onSnapshot(query(driversCol, where('championship', '==', championshipId)), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.filter(d => d.championship === championshipId).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  }, err => console.error('Drivers query error:', err));
}

/** Private contact details (email/phone) — stewards only (owner, 2 Nov). */
const driverContactsCol = collection(db, 'championships', 'data', 'driverContacts');
export function onDriverContacts(championshipId, cb) {
  return onSnapshot(query(driverContactsCol, where('championship', '==', championshipId)), snap => {
    const m = {};
    snap.docs.forEach(d => { m[d.id] = d.data(); });
    cb(m);
  }, err => console.error('Driver contacts query error:', err));
}
export const linkDriverAccount = httpsCallable(functions, 'linkDriverAccount');
export const saveDriverContact = httpsCallable(functions, 'saveDriverContact');
export const migrateDriverContacts = httpsCallable(functions, 'migrateDriverContacts');

export async function createDriver(data) {
  const ref = await addDoc(driversCol, cleanData({ ...data, status: 'active', createdAt: serverTimestamp() }));
  return ref.id;
}

export async function updateDriver(id, data) {
  await updateDoc(doc(driversCol, id), cleanData({ ...data, updatedAt: serverTimestamp() }));
}

export async function deleteDriver(id) {
  await deleteDoc(doc(driversCol, id));
  /* Their private contact goes too, so they can sign up again (owner, 2 Nov). */
  await deleteDoc(doc(driverContactsCol, id)).catch(() => null);
}

/** Mark a driver as advanced from a specific event/round */
export async function advanceDriver(driverId, eventId) {
  await updateDoc(doc(driversCol, driverId), {
    advancedFrom: arrayUnion(eventId),
    updatedAt: serverTimestamp(),
  });
}

/** Remove advancement for a driver from a specific event/round */
export async function unadvanceDriver(driverId, eventId) {
  await updateDoc(doc(driversCol, driverId), {
    advancedFrom: arrayRemove(eventId),
    updatedAt: serverTimestamp(),
  });
}

// =============================================
// EVENTS
// =============================================
export function onEvents(championshipId, cb) {
  return onSnapshot(query(eventsCol, where('championship', '==', championshipId)), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.filter(e => e.championship === championshipId).sort((a, b) => {
      // Primary: manual sortOrder (nulls go last)
      const aO = a.sortOrder ?? 9999;
      const bO = b.sortOrder ?? 9999;
      if (aO !== bO) return aO - bO;
      // Fallback: start date
      const aD = a.scheduledDate?.toMillis ? a.scheduledDate.toMillis() : 0;
      const bD = b.scheduledDate?.toMillis ? b.scheduledDate.toMillis() : 0;
      return aD - bD;
    }));
  }, err => console.error('Events query error:', err));
}

export async function createEvent(data) {
  const ref = await addDoc(eventsCol, cleanData({ ...data, status: 'upcoming', createdAt: serverTimestamp() }));
  return ref.id;
}

export async function updateEvent(id, data) {
  await updateDoc(doc(eventsCol, id), cleanData({ ...data, updatedAt: serverTimestamp() }));
}

export async function deleteEvent(id) {
  await deleteDoc(doc(eventsCol, id));
}

// =============================================
// RESULTS
// =============================================
export function onResults(eventId, cb) {
  return onSnapshot(query(resultsCol, where('event', '==', eventId)), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.filter(r => r.event === eventId).sort((a, b) => (a.position || 0) - (b.position || 0)));
  }, err => console.error('Results query error:', err));
}

export async function getAllResults(championshipId) {
  const eventsSnap = await getDocs(query(eventsCol, where('championship', '==', championshipId)));
  const eventIds = eventsSnap.docs.map(d => d.id);
  if (eventIds.length === 0) return [];

  /* Result docs carry an `event` foreign key but no championship, so fetch by
     event. Firestore caps `in` at 30 values, hence the chunking; the chunks run
     together rather than in series. */
  const chunks = [];
  for (let i = 0; i < eventIds.length; i += 30) chunks.push(eventIds.slice(i, i + 30));

  const snaps = await Promise.all(
    chunks.map(chunk => getDocs(query(resultsCol, where('event', 'in', chunk))))
  );

  return snaps.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

// =============================================
// PENALTIES
// =============================================
export function onPenalties(championshipId, cb) {
  /* Scoped by championship. This previously ignored the argument entirely and
     listed penalties from every championship ever run. Penalties written before
     the field existed need backfillPenaltyChampionships running once. */
  return onSnapshot(query(penaltiesCol, where('championship', '==', championshipId)), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.sort((a, b) => {
      const aT = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bT = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bT - aT;
    }));
  }, err => console.error('Penalties query error:', err));
}

export async function updatePenaltyStatus(id, status) {
  await updateDoc(doc(penaltiesCol, id), { status, updatedAt: serverTimestamp() });
}

// =============================================
// LEADERBOARDS
// =============================================
export function onLeaderboard(leaderboardId, cb) {
  return onSnapshot(doc(leaderboardCol, leaderboardId), d => {
    cb(d.exists() ? { id: d.id, ...d.data() } : null);
  }, err => console.error('Leaderboard query error:', err));
}

// =============================================
// SPONSORS (shared across championships via `championships` array)
// =============================================
export function onSponsors(cb) {
  return onSnapshot(sponsorsCol, snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.sort((a, b) => (a.order || 0) - (b.order || 0)));
  }, err => console.error('Sponsors query error:', err));
}

/** Get sponsors for a specific championship */
export function onSponsorsByChampionship(championshipId, cb) {
  return onSnapshot(query(sponsorsCol, where('championships', 'array-contains', championshipId)), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.sort((a, b) => (a.order || 0) - (b.order || 0)));
  }, err => console.error('Sponsors query error:', err));
}

export async function createSponsor(data) {
  const ref = await addDoc(sponsorsCol, cleanData({ ...data, createdAt: serverTimestamp() }));
  return ref.id;
}

export async function updateSponsor(id, data) {
  await updateDoc(doc(sponsorsCol, id), cleanData({ ...data, updatedAt: serverTimestamp() }));
}

export async function deleteSponsor(id) {
  await deleteDoc(doc(sponsorsCol, id));
}

// =============================================
// RULES
// =============================================
export function onRules(cb) {
  return onSnapshot(rulesCol, snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.sort((a, b) => {
      const aT = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bT = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bT - aT;
    }));
  }, err => console.error('Rules query error:', err));
}

export async function createRule(data) {
  const ref = await addDoc(rulesCol, cleanData({ ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
  return ref.id;
}

export async function updateRule(id, data) {
  await updateDoc(doc(rulesCol, id), cleanData({ ...data, updatedAt: serverTimestamp() }));
}

export async function deleteRule(id) {
  await deleteDoc(doc(rulesCol, id));
}

// =============================================
// PRIZES
// =============================================
export function onPrizes(championshipId, cb) {
  return onSnapshot(query(prizesCol, where('championship', '==', championshipId)), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(all.sort((a, b) => (a.position || 0) - (b.position || 0)));
  }, err => console.error('Prizes query error:', err));
}

export async function createPrize(data) {
  const ref = await addDoc(prizesCol, cleanData({ ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
  return ref.id;
}

export async function updatePrize(id, data) {
  await updateDoc(doc(prizesCol, id), cleanData({ ...data, updatedAt: serverTimestamp() }));
}

export async function deletePrize(id) {
  await deleteDoc(doc(prizesCol, id));
}

// =============================================
// CLOUD FUNCTION CALLS
// =============================================
export const submitResults = httpsCallable(functions, 'submitResults');
export const awardPenalty = httpsCallable(functions, 'awardPenalty');
export const recomputeLeaderboard = httpsCallable(functions, 'recomputeLeaderboard');
export const authenticateSteward = httpsCallable(functions, 'authenticateSteward');
export const sendChampionshipAnnouncement = httpsCallable(functions, 'sendChampionshipAnnouncement');
export const sendEventReminder = httpsCallable(functions, 'sendEventReminder');

// =============================================
// HELPERS
// =============================================

/** Parse time string M:SS.mmm or H:MM:SS.mmm to milliseconds */
export function parseTimeToMs(timeStr) {
  if (!timeStr) return null;
  const short = timeStr.match(/^(\d+):(\d{2})\.(\d{1,3})$/);
  if (short) {
    const [, m, s, ms] = short;
    return parseInt(m) * 60000 + parseInt(s) * 1000 + parseInt(ms.padEnd(3, '0'));
  }
  const long = timeStr.match(/^(\d+):(\d{2}):(\d{2})\.?(\d{0,3})$/);
  if (long) {
    const [, h, m, s, ms] = long;
    return parseInt(h) * 3600000 + parseInt(m) * 60000 + parseInt(s) * 1000 + parseInt((ms || '0').padEnd(3, '0'));
  }
  return null;
}

/** Format milliseconds to M:SS.mmm */
export function formatMs(ms) {
  if (ms == null) return '—';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const mil = ms % 1000;
  return `${m}:${String(s).padStart(2, '0')}.${String(mil).padStart(3, '0')}`;
}
